class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedBestSellerResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.bestAbortController = new AbortController();
    this.searchTerm = '';
    this._productsWheelBoundTo = null;
    this._productsWheelHandler = null;

    this.setupEventListeners();
  }

  normalizeTerm(term) {
    return (term || '').toLowerCase().trim();
  }

  normalizeSearchText(text) {
    // Keep matching case-insensitive and also normalize common synonyms.
    // Example: "trousers" and "pants" should behave like the same query.
    return this.normalizeTerm(text)
      .replace(/\btrousers?\b/g, 'pant')
      .replace(/\bpants?\b/g, 'pant');
  }

  termTokens(term) {
    return this.normalizeSearchText(term).split(/\s+/g).filter(Boolean);
  }

  textMatchesTokens(text, tokens) {
    const t = this.normalizeSearchText(text);
    if (!tokens.length) return true;
    return tokens.every((tok) => {
      if (t.includes(tok)) return true;
      if (tok.length > 3 && tok.endsWith('s')) {
        const singular = tok.slice(0, -1);
        if (singular && t.includes(singular)) return true;
      }
      return false;
    });
  }

  scoreTitleMatch(title, term) {
    const t = this.normalizeSearchText(title);
    const q = this.normalizeSearchText(term);
    if (!q) return 0;
    if (t === q) return 1000;
    if (t.startsWith(q)) return 900;
    if (t.includes(` ${q}`)) return 850;
    if (t.includes(q)) return 800;
    const tokens = q.split(/\s+/g).filter(Boolean);
    if (!tokens.length) return 0;
    const hits = tokens.reduce((acc, tok) => acc + (t.includes(tok) ? 1 : 0), 0);
    // Encourage multi-token matches, but keep it modest.
    return hits * 50;
  }

  scoreFieldMatch(text, term, { exact, startsWith, wordStart, contains, tokenHit }) {
    const t = this.normalizeSearchText(text);
    const q = this.normalizeSearchText(term);
    if (!q) return 0;
    if (!t) return 0;
    if (t === q) return exact;
    if (t.startsWith(q)) return startsWith;
    if (t.includes(` ${q}`)) return wordStart;
    if (t.includes(q)) return contains;
    const tokens = this.termTokens(q);
    if (!tokens.length) return 0;
    const hits = tokens.reduce((acc, tok) => acc + (t.includes(tok) ? 1 : 0), 0);
    return hits * tokenHit;
  }

  isLuxeFromTags(tags) {
    const v = this.normalizeTerm(tags);
    return v.includes('luxe') || v.includes('luxury') || v.includes('skipp-luxe') || v.includes('skipp_luxe');
  }

  normalizeVendorKey(vendor) {
    // Normalize punctuation/ampersands so matching is robust.
    return this.normalizeTerm(vendor)
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  isExclusiveVendor(vendorRaw) {
    const key = this.normalizeVendorKey(vendorRaw);
    return key === 'lords and kings' || key === 'street junkies' || key === 'cop underdog';
  }

  getProductMetaFromLi(li) {
    return {
      title: li?.getAttribute?.('data-title') || li?.textContent || '',
      type: li?.getAttribute?.('data-type') || '',
      description: li?.getAttribute?.('data-description') || '',
      tags: li?.getAttribute?.('data-tags') || '',
      vendor: li?.getAttribute?.('data-vendor') || '',
    };
  }

  productMatchesTerm(li, term) {
    const q = this.normalizeSearchText(term);
    if (!q) return true;

    const meta = this.getProductMetaFromLi(li);
    const title = this.normalizeSearchText(meta.title);
    if (title.includes(q)) return true; // title always wins

    const tokens = this.termTokens(q);
    const combined = `${meta.title} ${meta.type} ${meta.description}`;
    return this.textMatchesTokens(combined, tokens);
  }

  scoreProduct(li, term) {
    const meta = this.getProductMetaFromLi(li);
    const titleScore = this.scoreTitleMatch(meta.title, term);
    const typeScore = this.scoreFieldMatch(meta.type, term, {
      exact: 420,
      startsWith: 330,
      wordStart: 280,
      contains: 220,
      tokenHit: 40,
    });
    const descScore = this.scoreFieldMatch(meta.description, term, {
      exact: 140,
      startsWith: 110,
      wordStart: 90,
      contains: 80,
      tokenHit: 10,
    });

    return titleScore + typeScore + descScore;
  }

  applyClientFiltering() {
    const term = this.searchTerm;

    // Collections pills
    const collections = this.querySelectorAll('#predictive-search-results-collections-list [data-title]');
    collections.forEach((li) => {
      const title = li.getAttribute('data-title') || li.textContent || '';
      const ok = this.textMatchesTokens(title, this.termTokens(term));
      li.style.display = ok ? '' : 'none';
    });

    // Products row
    const products = this.querySelectorAll('#predictive-search-results-products-list [data-title]');
    products.forEach((li) => {
      const meta = this.getProductMetaFromLi(li);
      const ok = !this.isLuxeFromTags(meta.tags) && this.productMatchesTerm(li, term);
      li.style.display = ok ? '' : 'none';
    });
  }

  rerankCollections() {
    const list = this.querySelector('#predictive-search-results-collections-list');
    if (!list) return;

    const term = this.searchTerm;
    const items = Array.from(list.querySelectorAll(':scope > li'));
    if (items.length < 2) return;

    const scored = items.map((el, idx) => {
      const title = el.getAttribute('data-title') || el.textContent || '';
      return { el, idx, score: this.scoreTitleMatch(title, term) };
    });

    scored.sort((a, b) => (b.score - a.score) || (a.idx - b.idx));
    scored.forEach(({ el }) => list.appendChild(el));
  }

  rerankProducts() {
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    const term = this.searchTerm;
    const items = Array.from(list.querySelectorAll(':scope > li'));
    if (items.length < 2) return;

    const scored = items.map((el, idx) => {
      // Keep stable ordering for equal scores via idx.
      return { el, idx, score: this.scoreProduct(el, term) };
    });

    scored.sort((a, b) => (b.score - a.score) || (a.idx - b.idx));
    scored.forEach(({ el }) => list.appendChild(el));
  }

  getVendorKeyFromProductLi(li) {
    const v = li?.getAttribute?.('data-vendor') || '';
    return this.normalizeVendorKey(v);
  }

  diversifyProductsByVendor({ maxItems = 18, initialCapPerVendor = 2, minGap = 4 } = {}) {
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    const allItems = Array.from(list.querySelectorAll(':scope > li'));
    if (allItems.length < 3) return;

    const visibleItems = allItems.filter((el) => window.getComputedStyle(el).display !== 'none');
    if (visibleItems.length < 3) return;

    const vendorOrder = [];
    const queues = new Map();
    for (const el of visibleItems) {
      const vendor = this.getVendorKeyFromProductLi(el) || '__unknown__';
      if (!queues.has(vendor)) {
        queues.set(vendor, []);
        vendorOrder.push(vendor);
      }
      queues.get(vendor).push(el);
    }

    const taken = Object.create(null);
    const picked = [];
    const lastVendors = [];

    const tryPickWithCap = (cap) => {
      for (let pass = 0; pass < 2; pass++) {
        for (const vendor of vendorOrder) {
          const q = queues.get(vendor);
          if (!q || q.length === 0) continue;
          if ((taken[vendor] || 0) >= cap) continue;
          if (pass === 0 && minGap > 0 && lastVendors.slice(-minGap).includes(vendor)) continue;

          const el = q.shift();
          taken[vendor] = (taken[vendor] || 0) + 1;
          picked.push(el);
          lastVendors.push(vendor);
          return true;
        }
      }
      return false;
    };

    const tryPickAny = () => {
      for (let pass = 0; pass < 2; pass++) {
        for (const vendor of vendorOrder) {
          const q = queues.get(vendor);
          if (!q || q.length === 0) continue;
          if (pass === 0 && minGap > 0 && lastVendors.slice(-minGap).includes(vendor)) continue;

          const el = q.shift();
          taken[vendor] = (taken[vendor] || 0) + 1;
          picked.push(el);
          lastVendors.push(vendor);
          return true;
        }
      }
      return false;
    };

    // Phase 1: 1 per vendor (maximize brand variety first)
    while (picked.length < Math.min(maxItems, visibleItems.length)) {
      const progressed = tryPickWithCap(1);
      if (!progressed) break;
    }

    // Phase 2: up to 2 per vendor (your requested cap)
    while (picked.length < Math.min(maxItems, visibleItems.length)) {
      const progressed = tryPickWithCap(initialCapPerVendor);
      if (!progressed) break;
    }

    // Phase 3: if still not full, allow more per vendor, but spaced out
    while (picked.length < Math.min(maxItems, visibleItems.length)) {
      const progressed = tryPickAny();
      if (!progressed) break;
    }

    // Append any remaining visible items (if any) to keep list complete.
    const pickedSet = new Set(picked);
    const remainingVisible = visibleItems.filter((el) => !pickedSet.has(el));
    const finalOrder = [...picked, ...remainingVisible];

    finalOrder.forEach((el) => list.appendChild(el));
  }

  maskExclusiveVendorsInResults() {
    // Ensure vendor masking is applied even after the best-seller HTML replacement.
    const productLis = this.querySelectorAll('#predictive-search-results-products-list > li[data-vendor]');
    productLis.forEach((li) => {
      const vendorRaw = li.getAttribute('data-vendor') || '';
      if (!this.isExclusiveVendor(vendorRaw)) return;
      const vendorEl = li.querySelector('.predictive-search__item-vendor');
      if (vendorEl) vendorEl.textContent = 'EXCLUSIVE BRAND';
    });
  }

  setupProductsWheelScroll() {
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    // Rebind if list node changed (innerHTML replaces it).
    if (this._productsWheelBoundTo && this._productsWheelBoundTo !== list && this._productsWheelHandler) {
      this._productsWheelBoundTo.removeEventListener('wheel', this._productsWheelHandler);
      this._productsWheelBoundTo = null;
      this._productsWheelHandler = null;
    }

    if (this._productsWheelBoundTo === list && this._productsWheelHandler) return;

    const handler = (e) => {
      if (list.scrollWidth <= list.clientWidth) return;

      // Only hijack "vertical" wheel to scroll horizontally.
      const dy = e.deltaY || 0;
      const dx = e.deltaX || 0;
      if (Math.abs(dy) <= Math.abs(dx)) return;

      list.scrollLeft += dy;
      e.preventDefault();
    };

    list.addEventListener('wheel', handler, { passive: false });
    this._productsWheelBoundTo = list;
    this._productsWheelHandler = handler;
  }

  postProcessResults() {
    this.applyClientFiltering();
    this.rerankCollections();
    this.rerankProducts();
    // Diversify the first set heavily so users see many brands early.
    this.diversifyProductsByVendor({ maxItems: 24, initialCapPerVendor: 2, minGap: 4 });
    this.maskExclusiveVendorsInResults();
    this.setupProductsWheelScroll();
  }

  getBestSellerResults(searchTerm) {
    const term = this.normalizeTerm(searchTerm);
    const key = term.replace(/\s+/g, '-');
    if (!term) return Promise.resolve(null);

    if (this.cachedBestSellerResults[key]) return Promise.resolve(this.cachedBestSellerResults[key]);

    this.bestAbortController.abort();
    this.bestAbortController = new AbortController();

    const params = new URLSearchParams({
      q: term,
      type: 'product',
      'options[prefix]': 'last',
      'options[fields]': 'title,product_type,variants.title,body',
      sort_by: 'best-selling',
      view: 'best-suggestions',
    });

    return fetch(`/search?${params.toString()}`, { signal: this.bestAbortController.signal })
      .then((r) => (r.ok ? r.text() : null))
      .then((text) => {
        if (!text) return null;
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const root = doc.querySelector('[data-best-search-suggestions]');
        if (!root) return null;

        const pills = root.querySelector('[data-best-suggestions-pills]');
        const products = root.querySelector('[data-best-products]');

        const payload = {
          pillsHtml: pills ? pills.innerHTML : '',
          productsHtml: products ? products.innerHTML : '',
        };

        this.cachedBestSellerResults[key] = payload;
        return payload;
      })
      .catch(() => null);
  }

  async enhanceWithBestSellers() {
    // Only try on longer terms to reduce noise.
    const requested = this.normalizeTerm(this.searchTerm);
    if (requested.length < 2) return;

    const payload = await this.getBestSellerResults(requested);
    if (!payload) return;

    // Only apply if the term hasn't changed since request started.
    if (this.normalizeTerm(this.searchTerm) !== requested) return;

    const pillsList = this.querySelector('#predictive-search-results-suggestions-list');
    const productsList = this.querySelector('#predictive-search-results-products-list');

    if (pillsList && payload.pillsHtml) pillsList.innerHTML = payload.pillsHtml;
    if (productsList && payload.productsHtml) productsList.innerHTML = payload.productsHtml;

    this.postProcessResults();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Always abort the previous in-flight request so results never "lag behind" typing.
    this.abortController.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
    });

    // Products-only: faster, cleaner, and designed around swipeable product suggestions.
    params.set('resources[type]', 'product,collection');
    // Request a larger pool so we can diversify vendors client-side.
    params.set('resources[limit]', '80');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'hide');
    // Bias to product/collection names (avoid tag-like matches)
    params.set('resources[options][fields]', 'title,product_type,variants.title,body');

    fetch(`${routes.predictive_search_url}?${params.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();

    this.postProcessResults();
    // Re-rank products by best-selling for this keyword.
    this.enhanceWithBestSellers();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
